/**
 * Source-prefixed URL utilities
 *
 * All market/event routes use a source prefix: /polymarket/... or /kalshi/...
 * This module provides helpers to generate correct URLs based on the active source.
 */

export type Source = "polymarket" | "kalshi" | "predict" | "hyperliquid";

/**
 * Generate a source-prefixed URL.
 *
 * @example
 * sourceUrl("/markets/trending", "polymarket") → "/polymarket/markets/trending"
 * sourceUrl("/markets/trending", "kalshi")     → "/kalshi/markets/trending"
 * sourceUrl("/event/kalshi/KXTICKER", "kalshi") → "/kalshi/event/KXTICKER"
 * sourceUrl("/market/some-slug", "polymarket")  → "/polymarket/market/some-slug"
 */
export function sourceUrl(path: string, source: Source): string {
  // Normalize: /event/kalshi/TICKER → /kalshi/event/TICKER
  if (path.startsWith("/event/kalshi/")) {
    const ticker = path.slice("/event/kalshi/".length);
    return `/kalshi/event/${ticker}`;
  }

  // Already prefixed — return as-is
  if (
    path.startsWith("/polymarket/") ||
    path.startsWith("/kalshi/") ||
    path.startsWith("/predictfun/") ||
    path.startsWith("/hyperliquid/")
  ) {
    return path;
  }

  // Map internal 'predict' source to '/predictfun/' URL prefix
  const urlPrefix = source === 'predict' ? 'predictfun' : source;
  return `/${urlPrefix}${path}`;
}

/**
 * Extract source from a pathname prefix.
 * Returns null if no source prefix found.
 */
export function sourceFromPathname(pathname: string): Source | null {
  if (pathname.startsWith("/kalshi/")) return "kalshi";
  if (pathname.startsWith("/polymarket/")) return "polymarket";
  if (pathname.startsWith("/predictfun/")) return "predict";
  if (pathname.startsWith("/hyperliquid/") || pathname === "/hyperliquid") return "hyperliquid";
  return null;
}

/**
 * Extract Kalshi event ticker from a market ticker.
 * Market tickers: SERIES-NUM-SUFFIX (e.g., KXSENATETXD-26-JTAL)
 * Event tickers: SERIES-NUM (e.g., KXSENATETXD-26) or just SERIES (e.g., KXFEDRATE)
 */
export function extractKalshiEventTicker(ticker: string): string {
  const parts = ticker.split('-');
  if (parts.length >= 3) {
    return parts.slice(0, 2).join('-');
  }
  return ticker;
}

/**
 * Build a URL for a Polymarket market/outcome.
 *
 * /polymarket/market/<slug> stays ONLY for SEO — no in-app navigation should hit it.
 * All clicks go to /polymarket/event/<eventSlug>?market=<slug>&outcome=<x>.
 * The event page handles both binary events (1 market) and multi-outcome transparently.
 *
 * Fallback: if eventSlug is missing, fall back to /market/<slug> so links still resolve.
 * `isMultiOutcome` is accepted for backwards-compat but no longer required.
 *
 * @example
 * marketPageUrl({ marketSlug: 'foo', eventSlug: 'bar', outcome: 'Yes' })
 *   → "/polymarket/event/bar?market=foo&outcome=yes"
 * marketPageUrl({ marketSlug: 'foo' })  // no eventSlug
 *   → "/polymarket/market/foo"
 */
// Sports slug prefixes — shared with EventCard.tsx
const SPORTS_SLUG_PREFIXES = [
  "nba-","nhl-","nfl-","mlb-","mls-","wnba-","cbb-","cfb-","cwbb-","ahl-",
  "epl-","lal-","bun-","fl1-","sea-","ucl-","uel-","ere-","por-","tur-",
  "mex-","bra-","arg-","col1-","chi-","per1-","aus-","kor-","spl-","den-",
  "nor-","cze1-","rou1-","mar1-","rus-","efa-","efl-",
  "cs2-","lol-","val-","dota2-","r6siege-","mlbb-","ow-","hok-","rl-","sc2-",
  "atp-","wta-","ufc-","cricipl-","criclcl-","cricpsl-","crint-",
  "khl-","ruurc-","rueuchamp-","rusrp-","formula1-","wbc-","kbo-",
  // FIFA World Cup 2026 — partidos `fifwc-<home>-<away>-<date>` (+ sub-markets
  // `…-mex`, `…-more-markets`). Routea a /sports/ como el resto de deportes.
  "fifwc-",
];

export function isSportsSlug(slug: string): boolean {
  const lower = slug.toLowerCase();
  return SPORTS_SLUG_PREFIXES.some(p => lower.startsWith(p));
}

// FIFA World Cup match event slug = `fifwc-<home>-<away>-<YYYY-MM-DD>`. Los
// sub-markets traen un sufijo extra (`-mex`, `-more-markets`, `-total-corners`,
// `-exact-score-1-3`, …) y el event_slug puede llegar null (ej. filas de
// user_activity). Recortamos al evento para que el link aterrice en
// /sports/<event> y no en el slug del sub-market (que no resuelve ahí).
const FIFWC_EVENT_RE = /^(fifwc-[a-z0-9]+-[a-z0-9]+-\d{4}-\d{2}-\d{2})/i;
export function worldCupEventSlug(slug: string): string {
  const m = slug.match(FIFWC_EVENT_RE);
  return m ? m[1] : slug;
}

export function marketPageUrl(params: {
  marketSlug: string | null | undefined;
  eventSlug?: string | null;
  outcome?: string | null;
  source?: Source;
  eventCategory?: string | null;
  /** @deprecated no longer required — event page handles both binary and multi-outcome */
  isMultiOutcome?: boolean;
}): string {
  const { marketSlug, eventSlug, outcome, source = 'polymarket', eventCategory } = params;
  if (!marketSlug) return '/';
  if (source === 'polymarket') {
    const outcomeParam = outcome ? outcome.toLowerCase() : '';
    // Prefer the event route — the event page renders both binary (1 market)
    // and multi-outcome layouts and shows the orderbook/trades/holders tabs
    // for either. When eventSlug is missing we fall back to marketSlug: for
    // single-market Polymarket events the two slugs coincide, so the URL
    // still resolves to the right page without a round-trip to the market
    // detail page.
    let slugForEvent = eventSlug || marketSlug;
    const qs = outcomeParam ? `?market=${marketSlug}&outcome=${outcomeParam}` : `?market=${marketSlug}`;
    const isSports = isSportsSlug(slugForEvent) || eventCategory?.toLowerCase() === 'sports';
    if (isSports) {
      // WC: si slugForEvent es un sub-market (`fifwc-…-mex`) o el event_slug
      // llegó null y caímos al market slug, recortar al evento real.
      slugForEvent = worldCupEventSlug(slugForEvent);
      return `/sports/${slugForEvent}${qs}`;
    }
    return `/polymarket/event/${slugForEvent}${qs}`;
  }
  if (source === 'predict' && eventSlug) {
    return `/predictfun/event/${eventSlug}`;
  }
  if (source === 'hyperliquid') {
    // HL portfolio rows store the YES outcome's slug as marketSlug.
    // The event page handles both YES and NO sides; no outcome param
    // needed. Falls back to /hyperliquid/market/<slug> if the event
    // page isn't built for this slug yet.
    return `/hyperliquid/event/${marketSlug}`;
  }
  if (source === 'kalshi') {
    // Kalshi market tickers (e.g. KXLLM1-26APR25-ANTH) need the event ticker
    // (KXLLM1-26APR25) as the URL slug — that's where the event page lives.
    // marketSlug here is typically the kalshi_ticker stored in DB; derive
    // the event ticker locally so a Kalshi REDEEM activity row routes to
    // /kalshi/event/<eventTicker>?market=<marketTicker> instead of
    // /polymarket/event/<marketTicker> (the previous hardcoded source bug).
    const eventTicker = eventSlug || extractKalshiEventTicker(marketSlug);
    const qs = `?market=${marketSlug}${outcome ? `&outcome=${outcome.toLowerCase()}` : ''}`;
    return `/kalshi/event/${eventTicker}${qs}`;
  }
  return sourceUrl(`/market/${marketSlug}`, source);
}

/**
 * Strip the source prefix from a pathname to get the internal route.
 *
 * @example
 * stripSourcePrefix("/polymarket/markets/trending") → "/markets/trending"
 * stripSourcePrefix("/kalshi/event/TICKER")         → "/event/TICKER"
 * stripSourcePrefix("/portfolio")                   → "/portfolio"
 */
export function stripSourcePrefix(pathname: string): string {
  if (pathname.startsWith("/polymarket/")) return pathname.slice("/polymarket".length);
  if (pathname.startsWith("/kalshi/")) return pathname.slice("/kalshi".length);
  if (pathname.startsWith("/predictfun/")) return pathname.slice("/predictfun".length);
  if (pathname.startsWith("/hyperliquid/")) return pathname.slice("/hyperliquid".length);
  return pathname;
}
